import { forwardRef, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { OTP_LENGTH } from '@pidelo/contracts';
import { cx } from '../../internal/cx.ts';
import { FieldShell, isInvalid, useFieldIds } from '../Field/Field.tsx';
import styles from './OtpInput.module.css';

export type OtpInputProps = {
  value: string;
  onChange: (code: string) => void;
  /** Called once when the last digit arrives (typed, pasted or filled by the phone). */
  onComplete?: (code: string) => void;
  length?: number;
  error?: ReactNode | boolean;
  hint?: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  className?: string;
  /** Read by screen readers when there is no visible label. */
  'aria-label'?: string;
};

export function otpDigits(text: string, length = OTP_LENGTH): string {
  return text.replace(/\D/g, '').slice(0, length);
}

/**
 * The six boxes of the WhatsApp code, as in the prototypes. One real input lies over the boxes, so
 * pasting the whole code and the phone's «autocompletar» work, and screen readers see one field.
 */
export const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(function OtpInput(
  { value, onChange, onComplete, length = OTP_LENGTH, error, hint, label, disabled, autoFocus, id, className, 'aria-label': ariaLabel = `Código de ${length} dígitos` },
  ref,
) {
  const ids = useFieldIds(id, hint, error);
  const invalid = isInvalid(error);
  const [focused, setFocused] = useState(false);
  const last = useRef(value);
  const code = otpDigits(value, length);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = otpDigits(event.target.value, length);
    const before = last.current;
    last.current = next;
    onChange(next);
    if (next.length === length && before.length !== length) onComplete?.(next);
  };
  last.current = code;

  return (
    <FieldShell ids={ids} label={label} hint={hint} error={error}>
      <div className={cx(styles.otp, className)} data-invalid={invalid ? '' : undefined} data-disabled={disabled ? '' : undefined}>
        <input
          ref={ref}
          id={ids.id}
          className={styles.input}
          value={code}
          onChange={handleChange}
          onFocus={event => {
            setFocused(true);
            const end = event.target.value.length;
            event.target.setSelectionRange(end, end);
          }}
          onBlur={() => setFocused(false)}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          aria-label={label ? undefined : ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={ids.describedBy}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <div className={styles.boxes} aria-hidden="true" style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}>
          {Array.from({ length }, (_, index) => (
            <span
              key={index}
              className={styles.box}
              data-full={index < code.length ? '' : undefined}
              data-current={focused && (index === code.length || (code.length === length && index === length - 1)) ? '' : undefined}
            >
              {code[index] ?? ''}
            </span>
          ))}
        </div>
      </div>
    </FieldShell>
  );
});
